import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, MapPin, SlidersHorizontal, Briefcase, Sparkles, ChevronLeft, ChevronRight, X } from "lucide-react";
import api from "../lib/api";
import JobCard from "../components/JobCard";
import JobCardSkeleton from "../components/JobCardSkeleton";
import { EVENT_SPECIALIZATIONS, FREELANCE_SPECIALIZATIONS } from "../data/specializations";
import { useAuth } from "../context/AuthContext";

const PAGE_SIZE = 9;

export default function Landing({ fixedType }) {
  const { user } = useAuth();
  const nav = useNavigate();

  // Managers don't need the public worker job feed — send them straight to their dashboard
  useEffect(() => {
    if (user?.role === "manager") {
      nav("/dashboard", { replace: true });
    }
  }, [user, nav]);
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  

  const [showSort, setShowSort] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(false);
  const [oppType, setOppType] = useState(fixedType || "");
  const [spec, setSpec] = useState("");
  const [sort, setSort] = useState("latest");
  const [radius, setRadius] = useState("");
  const [coords, setCoords] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetchJobs = useCallback(async (p = 1) => {
    const qs = new URLSearchParams({
      limit: String(PAGE_SIZE + 1),
      skip: String((p - 1) * PAGE_SIZE),
    });
    if (oppType) qs.set("opportunity_type", oppType);
    if (spec) qs.set("specialization", spec);
    if (sort) qs.set("sort", sort);
    if (coords && radius) {
      qs.set("lat", coords.lat);
      qs.set("lng", coords.lng);
      qs.set("radius_km", radius);
    }
    if (availableOnly) qs.set("available_only", "true");
    try {
      const { data } = await api.get(`/jobs?${qs.toString()}`);
      setHasMore(data.length > PAGE_SIZE);
      setJobs(data.slice(0, PAGE_SIZE));
    } catch {} finally {
      setLoading(false);
    }
  }, [oppType, spec, sort, radius, coords, availableOnly]); // eslint-disable-line

  useEffect(() => { setPage(1); fetchJobs(1); }, [oppType, spec, sort, radius, coords, availableOnly]); // eslint-disable-line
  useEffect(() => { fetchJobs(page); }, [page]); // eslint-disable-line

  // Poll every 10s, pause when tab hidden
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchJobs(page);
    }, 10000);
    return () => clearInterval(interval);
  }, [page, oppType, spec, sort, radius, coords, availableOnly]); // eslint-disable-line

  useEffect(() => {
    const onFocus = () => fetchJobs(page);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [page, oppType, spec, sort, radius, coords, availableOnly]); // eslint-disable-line

  const detectLocation = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { "Accept-Language": "en" } }
          );
          const d = await res.json();
          const addr = d.address;
          const city = addr.city || addr.town || addr.village || addr.county || "";
          setCoords({ lat: latitude, lng: longitude, name: city });
        } catch {
          setCoords({ lat: latitude, lng: longitude, name: "" });
        }
        if (!radius) setRadius("25");
        setSort("nearest");
        setPage(1);
      },
      () => {}
    );
  }, [radius]);

  const handleSortSelect = useCallback((value) => {
    if (value === "nearest" && !coords) {
      detectLocation();
      setShowSort(false);
    } else {
      setSort(value);
      setPage(1);
      setShowSort(false);
    }
  }, [coords, detectLocation]);

  const clearFilter = (type) => {
    if (type === "oppType") { setOppType(""); setSpec(""); setPage(1); }
    if (type === "spec") { setSpec(""); setPage(1); }
    if (type === "sort") { setSort("latest"); setPage(1); }
    if (type === "location") { setCoords(null); setRadius(""); if (sort === "nearest") setSort("latest"); setPage(1); }
    if (type === "available") { setAvailableOnly(false); setPage(1); }
  };

  const specChips = useMemo(() => {
    if (oppType === "event_staffing") return EVENT_SPECIALIZATIONS;
    if (oppType === "freelance") return FREELANCE_SPECIALIZATIONS;
    return [...EVENT_SPECIALIZATIONS.slice(0, 6), ...FREELANCE_SPECIALIZATIONS.slice(0, 6)];
  }, [oppType]);

  const sortLabel = sort === "highest_payment" ? "Highest pay" : sort === "nearest" ? "Nearest" : "";

  return (
    <div className="bg-background" data-testid="landing-page">
      

      {/* HERO */}
      <section className="px-3 sm:px-6 pt-4 sm:pt-6">
        <div className="mx-auto max-w-7xl">
          <div className="hero-card relative rounded-3xl overflow-hidden p-6 sm:p-10 lg:p-16">
            <div className="absolute inset-0 grid-bg opacity-[0.06]" />
            <div className="absolute top-10 right-10 w-32 h-32 rounded-full animate-float-slow opacity-30" style={{ background: "radial-gradient(circle, #ec4cb8, transparent 70%)" }} />
            <div className="absolute bottom-8 left-8 w-40 h-40 rounded-full animate-float-slow opacity-25" style={{ background: "radial-gradient(circle, #a855f7, transparent 70%)" }} />
            <div className="relative">
              <span className="label-eyebrow text-purple-300/80">Mobile-first gig economy</span>
              <h1 className="mt-4 font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl text-white max-w-3xl">
                Hire instantly.<br/>
                <span className="gradient-text">Earn instantly.</span>
              </h1>
              <p className="mt-5 text-sm sm:text-base text-slate-300 max-w-xl">
                EarnMate connects event managers, workers and skilled freelancers — catering crew, cameramen, designers, writers and more.
              </p>
              <form
                onSubmit={(e) => { e.preventDefault(); nav(`/jobs?q=${encodeURIComponent(q)}`); }}
                className="mt-7 max-w-2xl bg-white/8 backdrop-blur-md border border-white/10 rounded-full flex items-center p-1.5 gap-1"
                data-testid="hero-search-form"
              >
                <div className="flex-1 flex items-center gap-2 pl-4">
                  <Search className="w-4 h-4 text-muted-foreground" />
                  <input
                    value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Try 'cameraman', 'poster design'…"
                    className="flex-1 bg-transparent outline-none text-sm sm:text-base text-white placeholder:text-muted-foreground py-2"
                    data-testid="hero-search-input"
                  />
                </div>
                <button className="btn-primary !py-2 !px-4" data-testid="hero-search-submit">
                  Search <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* LATEST OPPORTUNITIES */}
      <section className="mx-auto max-w-7xl px-3 sm:px-6 py-12 sm:py-16">
        <div className="flex items-end justify-between mb-6 gap-3">
          <div>
            <span className="label-eyebrow text-muted-foreground">Now live</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">Latest opportunities</h2>
          </div>
          <Link to="/jobs" className="text-sm font-medium text-brand hover:underline whitespace-nowrap">View all →</Link>
        </div>

        <div className="space-y-3" data-testid="home-filters">
          {/* Row 1: Type (hidden when this page is locked to one workspace type) */}
          {!fixedType && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button onClick={() => { setOppType(""); setSpec(""); setPage(1); }} className={`chip ${oppType === "" ? "chip-active" : ""}`}>All</button>
              <button onClick={() => { setOppType(oppType === "event_staffing" ? "" : "event_staffing"); setSpec(""); setPage(1); }} className={`chip ${oppType === "event_staffing" ? "chip-active" : ""}`} data-testid="chip-event">
                <Briefcase className="w-3 h-3" /> Event Staffing
                {oppType === "event_staffing" && <X className="w-3 h-3 ml-0.5" onClick={e => { e.stopPropagation(); clearFilter("oppType"); }} />}
              </button>
              <button onClick={() => { setOppType(oppType === "freelance" ? "" : "freelance"); setSpec(""); setPage(1); }} className={`chip ${oppType === "freelance" ? "chip-active" : ""}`} data-testid="chip-freelance">
                <Sparkles className="w-3 h-3" /> Freelance
                {oppType === "freelance" && <X className="w-3 h-3 ml-0.5" onClick={e => { e.stopPropagation(); clearFilter("oppType"); }} />}
              </button>
            </div>
          )}
          {/* Row 2: Categories */}

          {/* Row 2: Categories */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button onClick={() => setShowCategories(s => !s)} className={`chip ${showCategories ? "chip-active" : ""}`}>
              Categories {showCategories ? "▲" : "▼"}
            </button>
            {spec && (
              <span className="chip chip-active">
                {spec} <button onClick={() => clearFilter("spec")}><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>

          {showCategories && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button onClick={() => { setSpec(""); setPage(1); setShowCategories(false); }} className={`chip ${spec === "" ? "chip-active" : ""}`}>All</button>
              {specChips.map(s => (
                <button key={s} onClick={() => { setSpec(s); setPage(1); setShowCategories(false); }} className={`chip ${spec === s ? "chip-active" : ""}`}>{s}</button>
              ))}
            </div>
          )}

          {/* Row 3: Sort + Available + Location */}
          <div className="flex flex-wrap items-center gap-2">

           {/* Sort */}
            <button onClick={() => setShowSort(s => !s)} className={`chip ${showSort ? "chip-active" : ""}`}>
              <SlidersHorizontal className="w-3.5 h-3.5" /> Sort: {sort === "highest_payment" ? "Highest pay" : sort === "nearest" ? "Nearest" : "Recent"} {showSort ? "▲" : "▼"}
            </button>
            {showSort && (
              <>
                {[["latest", "Recent"], ["highest_payment", "Highest pay"], ["nearest", "Nearest"]].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => handleSortSelect(v)}
                    className={`chip ${sort === v ? "chip-active" : ""}`}
                  >
                    {v === "nearest" && <MapPin className="w-3 h-3" />}
                    {l}
                    {sort === v && <span className="ml-1">✓</span>}
                    {v === "nearest" && !coords && <span className="text-[10px] opacity-60 ml-0.5">(needs location)</span>}
                  </button>
                ))}
              </>
            )}
            <div className="hidden sm:block w-px h-6 bg-border mx-1" />

            {/* Available only */}
            <button
              onClick={() => { setAvailableOnly(a => !a); setPage(1); }}
              className={`chip ${availableOnly ? "chip-active" : ""}`}
            >
              {availableOnly
                ? <>Open slots only <button onClick={e => { e.stopPropagation(); clearFilter("available"); }}><X className="w-3 h-3" /></button></>
                : "Open slots only"
              }
            </button>

            <div className="hidden sm:block w-px h-6 bg-border mx-1" />

            {/* Location */}
            <button
              onClick={() => coords ? clearFilter("location") : detectLocation()}
              className={`chip ${coords ? "chip-active" : ""}`}
              data-testid="chip-use-location"
            >
              <MapPin className="w-3.5 h-3.5" />
              {coords
                ? <>{coords.name || "Near me"} <X className="w-3 h-3" /></>
                : "Use my location"
              }
            </button>
            {coords && (
              <select
                value={radius}
                onChange={e => { setRadius(e.target.value); setPage(1); }}
                className="chip !cursor-pointer"
              >
                <option value="5">5 km</option>
                <option value="10">10 km</option>
                <option value="25">25 km</option>
                <option value="50">50 km</option>
              </select>
            )}
          </div>
        </div>

        {/* Job grid */}
        <div className="mt-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => <JobCardSkeleton key={i} />)}
            </div>
          ) : jobs.length === 0 ? (
            <div className="card-flat text-center py-16">
              <Briefcase className="w-10 h-10 mx-auto text-brand mb-3" />
              <p className="font-display text-xl">No matching opportunities</p>
              <p className="text-sm text-muted-foreground mt-1">
                {availableOnly
                  ? "No jobs with open slots right now. Try turning off 'Open slots only'."
                  : "Try a different filter — or be the first to post one."
                }
              </p>
              {user?.role !== "worker" && (
                <Link to="/post-job" className="btn-primary mt-5 inline-flex">+ Post a Job</Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {jobs.map(j => <JobCard key={j.id} job={j} />)}
            </div>
          )}
        </div>

        {/* Pagination */}
        {(page > 1 || hasMore) && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-outline !py-2 !px-3 disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm text-muted-foreground">Page {page}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={!hasMore} className="btn-outline !py-2 !px-3 disabled:opacity-40">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-3 sm:px-6 pb-16">
        <div className="rounded-3xl border border-border p-6 sm:p-10 grid md:grid-cols-2 gap-6 items-center bg-card">
          <div>
            <span className="label-eyebrow text-brand">For everyone</span>
           <h3 className="mt-2 font-display text-2xl sm:text-3xl tracking-tight">Staff. Manager. Freelancer.</h3>
            <p className="mt-2 text-sm text-muted-foreground">One platform, three ways to win. Apply to gigs, run events, or sell your skills — all in your pocket.</p>
          </div>
          <div className="flex flex-wrap gap-2 md:justify-end">
           <Link to="/jobs?type=event_staffing" className="btn-tint" data-testid="cta-event">Browse Events</Link>
            <Link to="/freelancers" className="btn-tint" data-testid="cta-freelance">Find Freelancers</Link>
            {user?.role !== "worker" && (
              <Link to="/post-job" className="btn-primary" data-testid="cta-post">Post a Job</Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
